import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchMembers, saveMemberDetails, fetchMemberById } from '../../services/masters/memberService';
import { ACTIONS } from '../../constants/apiEndpoints';

export const getMembers = createAsyncThunk(
  ACTIONS.member_master,
  async (_, thunkAPI) => {
    try {
      const response = await fetchMembers();
      if (response.data.statuscode !== 200) {
        return thunkAPI.rejectWithValue({
          message: `Failed to fetch members: ${response.data.message}`,
          statuscode: response.data.statuscode,
        });
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message,
        statuscode: error.response?.status || 500
      });
    }
  }
);

export const saveMember = createAsyncThunk(
  ACTIONS.member_save,
  async (memberData, thunkAPI) => {
    try {
      const response = await saveMemberDetails(memberData);
      if (response.data.statuscode !== 200) {
        return thunkAPI.rejectWithValue({
          message: `Failed to save member: ${response.data.message}`,
          statuscode: response.data.statuscode,
        });
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message,
        statuscode: error.response?.status || 500
      });
    }
  }
);

export const getMemberById = createAsyncThunk(
  ACTIONS.member_master + "_by_id",
  async (id, thunkAPI) => {
    try {
      const response = await fetchMemberById(id);
      if (response.data.statuscode !== 200) {
        return thunkAPI.rejectWithValue({
          message: `Failed to fetch member: ${response.data.message}`,
          statuscode: response.data.statuscode,
        });
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.response?.data?.message,
        statuscode: error.response?.status || 500
      });
    }
  }
);

const memberSlice = createSlice({
  name: 'member',
  initialState: {
    statuscode: 0,
    transactionId: null,
    message: null,
    description: null,
    data: {},
    options: [],
    saveStatus: 'idle',
    loading: false,
    error: null,
  },
  reducers: {
    resetSaveStatus: (state) => {
      state.saveStatus = 'idle';
    },
    //init Member
    setTransactionId: (state, action) => {
      state.transactionId = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMembers.fulfilled, (state, action) => {
        state.loading = false;
        const { statuscode, message, description, data } = action.payload;
        state.statuscode = statuscode;
        state.message = message;
        state.description = description;
        state.data = data;
      })
      .addCase(getMembers.rejected, (state, action) => {
        state.loading = false;
        // Handle "No records found" as a normal state, not an error
        if (action.payload?.message === 'No records found' || 
            action.payload?.errorCode === 'NO_RECORDS_FOUND') {
          state.error = null;
          state.data = { members: [] };
        } else {
          state.error = action.payload?.message || action.payload || 'An error occurred';
        }
      })

      //Save Member
      .addCase(saveMember.pending, (state) => {
        state.loading = true;
        state.saveStatus = 'loading';
        state.error = null;
      })
      .addCase(saveMember.fulfilled, (state, action) => {
        state.loading = false;
        const { statuscode, message, description, data } = action.payload;
        state.statuscode = statuscode;
        state.message = message;
        state.description = description;

        if (state.data?.members) {
          const index = state.data.members.findIndex(
            (member) => member.memberName === data.memberName
          );

          if (index !== -1) {
            // Override existing member
            state.data.members[index] = data;
          } else {
            // Push new member
            state.data.members.push(data);
          }

          state.saveStatus = 'success';
        }
      })
      .addCase(saveMember.rejected, (state, action) => {
        state.loading = false;
        state.saveStatus = 'error';
        state.error = action.payload?.message || action.payload || 'An error occurred';
      })
      // Get Member by ID
      .addCase(getMemberById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMemberById.fulfilled, (state, action) => {
        state.loading = false;
        // We don't need to update the state data here as we're just fetching for the form
      })
      .addCase(getMemberById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'An error occurred';
      });
  },
});

export const { resetSaveStatus , setTransactionId } = memberSlice.actions;
export default memberSlice.reducer;